import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plane, Building, Ticket, MapPin, Calendar, Users, AlertTriangle, Mail } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { apiFetch } from '@/lib/api'
import { parseTripDate } from '@/lib/dates'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CancellationModal } from '@/components/booking/CancellationModal'
import { FlightChangeModal } from '@/components/booking/FlightChangeModal'
import { FLIGHT_CANCELLATION_ENABLED, FLIGHT_CHANGES_ENABLED } from '@/lib/bookings-api'
import type { Trip, TripFlight, TripHotel, TripHotelCancellationPolicyTier } from '@/types/trip'

const SUPPORT_EMAIL = 'support@voy.earth'

export default function TripDetailPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [trip, setTrip] = useState<Trip | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<{
    type: 'flight' | 'hotel' | 'order'
    duffelOrderId?: string
    ratehawkOrderId?: string
    orderItemId?: string
    totalAmount?: number
  } | null>(null)
  const [changeTarget, setChangeTarget] = useState<{
    orderItemId: string
    slices?: TripFlight['slices']
    origin?: string
    destination?: string
    departureDate?: string
  } | null>(null)

  useEffect(() => {
    if (!user?.id || !orderId) return
    void load(user.id, orderId)
  }, [user?.id, orderId])

  async function load(userId: string, id: string) {
    setIsLoading(true)
    setError(null)
    try {
      const res = await apiFetch(`/api/v2/profile/${userId}/trips/${id}`)
      // 403 from the backend IDOR guard means the caller asked for someone
      // else's trip. Show the same fallback as a genuine 404 — do not surface
      // "Forbidden" to the user, which would hint at an ID enumeration surface.
      if (res.status === 404 || res.status === 403) {
        setError('Trip not found')
        return
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || 'Failed to load trip')
      }
      setTrip((await res.json()) as Trip)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trip')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <CenteredMessage message="Loading trip…" />
  }
  if (error || !trip) {
    return (
      <CenteredMessage
        message={error || 'Trip not found'}
        action={
          <Button variant="outline" onClick={() => navigate('/profile')}>
            Back to profile
          </Button>
        }
      />
    )
  }

  const startIso = trip.date_range?.start
  const endIso = trip.date_range?.end
  const startDate = startIso ? parseTripDate(startIso) : null
  const endDate = endIso ? parseTripDate(endIso) : null
  const daysUntil = startDate && trip.status === 'upcoming'
    ? Math.ceil((startDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null

  const dateRangeLabel = startDate && endDate
    ? `${fmtDate(startDate)} → ${fmtDate(endDate)}`
    : startDate
      ? fmtDate(startDate)
      : 'Dates to be confirmed'

  const total = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (trip.currency || 'USD').toUpperCase(),
    maximumFractionDigits: 2,
  }).format(trip.total_amount || 0)

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 py-6">
        <Link
          to="/profile"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to profile
        </Link>

        <header className="mt-4 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <StatusBadge status={trip.status} />
            {daysUntil !== null && daysUntil > 0 && (
              <span className="text-xs text-[#5B6FED] font-medium">
                in {daysUntil} {daysUntil === 1 ? 'day' : 'days'}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-semibold text-foreground">{trip.destination}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            {dateRangeLabel}
          </div>
        </header>

        <div className="mt-6 flex flex-col gap-4">
          {trip.flights.map((f) => <FlightBlock key={f.id} flight={f} />)}
          {trip.hotels.map((h) => <HotelBlock key={h.id} hotel={h} />)}

          <CostSummaryCard trip={trip} total={total} />

          <Card className="rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-col">
                <div className="text-sm font-semibold text-foreground">Need help with this trip?</div>
                <div className="text-xs text-muted-foreground">
                  {FLIGHT_CANCELLATION_ENABLED ? 'You can cancel individual bookings below, or contact support for assistance.' : 'Contact support to cancel a booking.'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(() => {
                  // Positive status check: only show cancel for confirmed/active flights.
                  // Avoids offering cancel on failed, void, or other non-cancellable states.
                  const cancellable = trip.flights.find(
                    f => f.duffel_order_id && (f.status === 'confirmed' || f.status === 'active' || !f.status),
                  )
                  if (!cancellable || (!FLIGHT_CANCELLATION_ENABLED && !FLIGHT_CHANGES_ENABLED)) return null
                  const firstSlice = cancellable.slices?.[0]
                  return (
                    <>
                      {FLIGHT_CHANGES_ENABLED && (
                        <Button
                          variant="outline"
                          onClick={() =>
                            setChangeTarget({
                              orderItemId: cancellable.id,
                              slices: cancellable.slices,
                              origin: firstSlice?.origin?.iata_code || cancellable.origin,
                              destination: firstSlice?.destination?.iata_code || cancellable.destination,
                              departureDate: cancellable.departure_date,
                            })
                          }
                        >
                          Change flight
                        </Button>
                      )}
                      {FLIGHT_CANCELLATION_ENABLED && (
                        <Button
                          variant="outline"
                          onClick={() =>
                            setCancelTarget({
                              type: 'flight',
                              duffelOrderId: cancellable.duffel_order_id!,
                              orderItemId: cancellable.id,
                              totalAmount: cancellable.price != null ? Number(cancellable.price) || undefined : undefined,
                            })
                          }
                        >
                          Cancel flight
                        </Button>
                      )}
                    </>
                  )
                })()}
                <Button asChild variant="outline">
                  <a href={`mailto:${SUPPORT_EMAIL}?subject=Trip%20${trip.id}`}>
                    <Mail className="h-4 w-4 mr-1" />
                    Contact support
                  </a>
                </Button>
              </div>
            </div>
          </Card>

          {cancelTarget && orderId && (
            <CancellationModal
              isOpen={!!cancelTarget}
              onClose={() => setCancelTarget(null)}
              onSuccess={() => {
                setCancelTarget(null)
                if (user?.id && orderId) load(user.id, orderId)
              }}
              orderId={orderId}
              itemType={cancelTarget.type}
              duffelOrderId={cancelTarget.duffelOrderId}
              orderItemId={cancelTarget.orderItemId}
              totalAmount={cancelTarget.totalAmount}
            />
          )}

          {changeTarget && (
            <FlightChangeModal
              isOpen={!!changeTarget}
              onClose={() => setChangeTarget(null)}
              onSuccess={() => {
                setChangeTarget(null)
                if (user?.id && orderId) load(user.id, orderId)
              }}
              orderItemId={changeTarget.orderItemId}
              slices={changeTarget.slices}
              currentOrigin={changeTarget.origin}
              currentDestination={changeTarget.destination}
              currentDepartureDate={changeTarget.departureDate}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function CenteredMessage({ message, action }: { message: string; action?: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="text-sm text-muted-foreground">{message}</div>
        {action}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: Trip['status'] }) {
  const tone: Record<string, string> = {
    upcoming: 'bg-[#5B6FED]/15 text-[#5B6FED]',
    active: 'bg-emerald-500/15 text-emerald-600',
    past: 'bg-muted text-muted-foreground',
    failed: 'bg-destructive/15 text-destructive',
  }
  const cls = tone[status] || 'bg-muted text-muted-foreground'
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  )
}

function FlightBlock({ flight }: { flight: TripFlight }) {
  const slices = flight.slices || []
  return (
    <Card className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2">
        <Plane className="h-4 w-4 text-[#5B6FED]" />
        <h2 className="text-base font-semibold text-foreground">
          {flight.airline || 'Flight'}
        </h2>
        {flight.status && <span className="text-[11px] uppercase text-muted-foreground">{flight.status}</span>}
      </div>

      <div className="mt-3 flex flex-col gap-3">
        {slices.length > 0 ? (
          slices.map((slice, i) => (
            <FlightSlice key={slice.departure ?? i} slice={slice} />
          ))
        ) : (
          <div className="text-sm text-muted-foreground">
            {flight.origin && flight.destination ? `${flight.origin} → ${flight.destination}` : 'Flight details unavailable'}
          </div>
        )}
      </div>

      <CardContent className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 p-0 text-xs">
        {flight.duffel_order_id && (
          <InfoRow label="Booking reference" value={flight.duffel_order_id} />
        )}
        <TicketsRow ticketNumbers={flight.ticket_numbers} />
      </CardContent>
    </Card>
  )
}

function FlightSlice({ slice }: { slice: NonNullable<TripFlight['slices']>[number] }) {
  const segments = slice.segments || []
  const originLabel = slice.origin?.iata_code || slice.origin?.city_name || slice.origin?.name || '—'
  const destLabel = slice.destination?.iata_code || slice.destination?.city_name || slice.destination?.name || '—'

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3">
      <div className="flex items-center justify-between text-sm">
        <div className="font-medium">{originLabel} → {destLabel}</div>
        {slice.departure && (
          <div className="text-xs text-muted-foreground">{fmtDate(parseTripDate(slice.departure))}</div>
        )}
      </div>
      {segments.length > 1 && (
        <div className="mt-2 flex flex-col gap-1 text-[11px] text-muted-foreground">
          {segments.map((seg, i) => {
            const prev = i > 0 ? segments[i - 1] : null
            const layoverMinutes = prev?.arriving_at && seg.departing_at
              ? Math.round((new Date(seg.departing_at).getTime() - new Date(prev.arriving_at).getTime()) / 60000)
              : null
            return (
              <div key={seg.departing_at ?? i}>
                {layoverMinutes !== null && layoverMinutes > 0 && (
                  <div className="text-amber-600">
                    Layover at {prev?.destination?.iata_code || prev?.destination?.city_name || '—'}: {fmtLayover(layoverMinutes)}
                  </div>
                )}
                <div>
                  {seg.origin?.iata_code || seg.origin?.city_name || '—'}
                  {' '}→{' '}
                  {seg.destination?.iata_code || seg.destination?.city_name || '—'}
                  {seg.operating_carrier?.iata_code && ` · ${seg.operating_carrier.iata_code}${seg.flight_number || ''}`}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function TicketsRow({ ticketNumbers }: { ticketNumbers: unknown }) {
  const tickets = normalizeTicketNumbers(ticketNumbers)
  if (tickets.length === 0) return null
  return (
    <div className="flex items-start gap-2">
      <Ticket className="h-3 w-3 mt-0.5 text-muted-foreground" />
      <div className="flex flex-col">
        <div className="text-muted-foreground">Ticket numbers</div>
        <div className="font-mono text-[11px] text-foreground break-all">
          {tickets.join(', ')}
        </div>
      </div>
    </div>
  )
}

function HotelBlock({ hotel }: { hotel: TripHotel }) {
  const policyTiers = normalizeCancellationPolicy(hotel.cancellation_policy)
  return (
    <Card className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2">
        <Building className="h-4 w-4 text-[#5B6FED]" />
        <h2 className="text-base font-semibold text-foreground">
          {hotel.hotel_name || 'Hotel'}
        </h2>
        {hotel.status && <span className="text-[11px] uppercase text-muted-foreground">{hotel.status}</span>}
      </div>

      {hotel.address && (
        <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 mt-0.5" />
          <span>{hotel.address}</span>
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        {hotel.check_in && <InfoRow label="Check-in" value={fmtDateOnly(hotel.check_in)} />}
        {hotel.check_out && <InfoRow label="Check-out" value={fmtDateOnly(hotel.check_out)} />}
        {hotel.room_type && <InfoRow label="Room" value={hotel.room_type} />}
        {hotel.ratehawk_order_id && (
          <InfoRow label="Booking reference" value={hotel.ratehawk_order_id} />
        )}
      </div>

      <GuestsList guests={hotel.guests} />

      {policyTiers.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-600">
            <AlertTriangle className="h-3 w-3" />
            Cancellation policy
          </div>
          <ul className="mt-2 flex flex-col gap-1 text-[11px] text-foreground">
            {policyTiers.map((tier, i) => (
              <li key={tier.start_at ?? tier.end_at ?? i}>
                {tier.start_at && <>From {fmtDateOnly(tier.start_at)}</>}
                {tier.end_at && <> until {fmtDateOnly(tier.end_at)}</>}
                {tier.penalty && (
                  <>
                    : penalty {tier.penalty.amount}
                    {tier.penalty.currency ? ` ${tier.penalty.currency}` : ''}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

function GuestsList({ guests }: { guests: unknown }) {
  if (!guests) return null
  const arr = Array.isArray(guests) ? guests : []
  if (arr.length === 0) return null
  return (
    <div className="mt-3 flex items-start gap-2 text-xs">
      <Users className="h-3 w-3 mt-0.5 text-muted-foreground" />
      <div className="flex flex-col">
        <div className="text-muted-foreground">Guests</div>
        <div className="text-foreground">
          {arr.map((g, i) => {
            const guest = (g ?? {}) as { first_name?: string; last_name?: string; name?: string }
            const name = [guest.first_name, guest.last_name].filter(Boolean).join(' ') || guest.name || `Guest ${i + 1}`
            return <div key={i}>{name}</div>
          })}
        </div>
      </div>
    </div>
  )
}

function CostSummaryCard({ trip, total }: { trip: Trip; total: string }) {
  return (
    <Card className="rounded-2xl border border-border bg-card p-5">
      <h2 className="text-base font-semibold text-foreground">Cost summary</h2>
      <div className="mt-3 flex flex-col gap-2 text-sm">
        {trip.flights.map((f) => (
          <CostLine key={f.id} label={`Flight · ${f.airline || 'Booking'}`} value={f.price} currency={trip.currency} />
        ))}
        {trip.hotels.map((h) => (
          <CostLine key={h.id} label={`Hotel · ${h.hotel_name || 'Booking'}`} value={h.price} currency={trip.currency} />
        ))}
        <div className="mt-1 flex items-center justify-between border-t border-border pt-2 font-semibold">
          <span>Total</span>
          <span>{total}</span>
        </div>
      </div>
    </Card>
  )
}

function CostLine({ label, value, currency }: { label: string; value: unknown; currency: string }) {
  const num = typeof value === 'string' ? parseFloat(value) : typeof value === 'number' ? value : null
  if (num === null || Number.isNaN(num)) return null
  const fmt = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'USD').toUpperCase(),
    maximumFractionDigits: 2,
  }).format(num)
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground truncate pr-2">{label}</span>
      <span className="text-foreground">{fmt}</span>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-[11px] text-foreground break-all">{value}</span>
    </div>
  )
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtDateOnly(iso: string): string {
  // Treat as a calendar date: extract the YYYY-MM-DD prefix and render it
  // as local noon, so UTC midnight timestamps (e.g. cancellation policy
  // tiers) don't shift to the previous day in timezones west of UTC.
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso)
  const d = match ? new Date(`${match[1]}T12:00:00`) : new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtLayover(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

function normalizeTicketNumbers(raw: unknown): string[] {
  if (!raw) return []
  if (Array.isArray(raw)) {
    return raw.flatMap((t) => {
      if (typeof t === 'string') return [t]
      if (t && typeof t === 'object') {
        const obj = t as Record<string, unknown>
        const num = obj.unique_identifier || obj.number || obj.ticket_number
        return typeof num === 'string' ? [num] : []
      }
      return []
    })
  }
  if (typeof raw === 'string') return [raw]
  return []
}

function normalizeCancellationPolicy(raw: unknown): TripHotelCancellationPolicyTier[] {
  if (!raw) return []
  if (Array.isArray(raw)) return raw as TripHotelCancellationPolicyTier[]
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    const tiers = obj.policies || obj.tiers || obj.cancellation_policies
    if (Array.isArray(tiers)) return tiers as TripHotelCancellationPolicyTier[]
  }
  return []
}
